// Task Title Template Dictionary
// Single source of truth for concierge-style titles used across:
// - TaskCard (Today, Daily Run)
// - Requests Queue
// - Reports / Concierge Briefing (web preview, email HTML, PDF)
//
// Usage: buildTaskTitle({ category, summary, guest_name })

export const TITLE_TEMPLATES = {
  arrival: {
    early_arrival: 'Early arrival — room status + plan',
    check_in_ready: 'Check-in readiness — confirm keys + welcome notes',
    room_not_ready: 'Room not ready — set expectation + timeline',
    late_checkout_request: 'Late checkout request — confirm options + pricing',
    general: 'Arrival support — confirm timing + access',
  },
  access: {
    locked_out: 'Access blocked — guest can\u2019t enter',
    lockbox_reset: 'Lockbox code reset — send new code',
    key_replacement: 'Key replacement — arrange handoff',
    gate_code: 'Gate code — confirm + send',
    wifi_code: 'Wi-Fi access — resend details',
    general: 'Access help — confirm entry plan',
  },
  housekeeping: {
    extra_towels: 'Extra towels — dispatch + confirm delivery',
    mid_stay_clean: 'Mid-stay clean — schedule + confirm window',
    linen_change: 'Linen change — arrange + confirm',
    urgent_clean: 'Housekeeping urgent — dispatch now',
    general: 'Housekeeping request — assign + confirm',
  },
  transport: {
    airport_pickup: 'Airport pickup — confirm time + driver',
    airport_dropoff: 'Airport drop-off — confirm pickup time',
    tour_booking: 'Tour booking — confirm availability + reserve',
    car_rental: 'Car rental — confirm options + arrange',
    general: 'Transport request — confirm details + book',
  },
  maintenance: {
    no_hot_water: 'No hot water — dispatch maintenance + ETA',
    wifi_issue: 'Wi-Fi issue — troubleshoot + update guest',
    ac_issue: 'A/C issue — dispatch + update guest',
    noise_complaint: 'Noise complaint — investigate + resolve',
    general: 'Maintenance request — dispatch + follow up',
  },
  booking_inquiry: {
    availability: 'Availability inquiry — confirm dates + rate',
    price_match: 'Price question — clarify + reply',
    general: 'Booking inquiry — respond + move forward',
  },
  in_stay_support: {
    comfort_issue: 'Guest comfort issue — respond + resolve',
    special_request: 'Special request — confirm + arrange',
    general: 'Guest support — respond + next step',
  },
  review: {
    reply_needed: 'Review reply — draft + post',
    follow_up: 'Review follow-up — investigate + respond',
  },
  other: 'Guest request — review + set next step',
};

// Map Task.category enum -> request_type
const CATEGORY_MAP = {
  ARRIVAL: 'arrival',
  ACCESS: 'access',
  HOUSEKEEPING: 'housekeeping',
  TRANSPORT: 'transport',
  MAINTENANCE: 'maintenance',
  BOOKING: 'booking_inquiry',
  INQUIRY: 'booking_inquiry',
  CONCIERGE: 'in_stay_support',
  IN_STAY_ISSUE: 'in_stay_support',
  BILLING: 'in_stay_support',
  REVIEW_RESPONSE: 'review',
  REVIEW: 'review',
  OTHER: 'other',
};

// Subtype detection — keyword matches against the summary text.
// Order matters: more specific patterns first.
const SUBTYPE_RULES = [
  // Access
  { type: 'access', subtype: 'locked_out', match: /(locked? out|can'?t (get )?in|can'?t (access|enter)|no entry|stuck outside)/i },
  { type: 'access', subtype: 'lockbox_reset', match: /(lockbox|lock\s?box)/i },
  { type: 'access', subtype: 'key_replacement', match: /(lost key|key replacement|new key|missing key)/i },
  { type: 'access', subtype: 'gate_code', match: /(gate code|gate access)/i },
  { type: 'access', subtype: 'wifi_code', match: /(wifi (code|password)|wi-fi (code|password)|wifi (login|details)|wi-fi (login|details))/i },
  // Maintenance
  { type: 'maintenance', subtype: 'no_hot_water', match: /(no hot water|hot water (not working|broken|out)|cold shower)/i },
  { type: 'maintenance', subtype: 'wifi_issue', match: /(wifi.*(not working|down|broken|slow|issue)|wi-fi.*(not working|down|broken|slow|issue))/i },
  { type: 'maintenance', subtype: 'ac_issue', match: /(a\/?c|air[- ]?conditioning|aircon).*(not working|broken|warm|issue|hot)|(no a\/?c)/i },
  { type: 'maintenance', subtype: 'noise_complaint', match: /(noise|loud|too loud|neighbou?r noise)/i },
  // Arrival
  { type: 'arrival', subtype: 'early_arrival', match: /(early (arrival|check.?in)|arriv(e|ing) early)/i },
  { type: 'arrival', subtype: 'room_not_ready', match: /(room (is )?not ready|room won'?t be ready)/i },
  { type: 'arrival', subtype: 'check_in_ready', match: /(check.?in (ready|confirmation)|ready for check.?in)/i },
  { type: 'arrival', subtype: 'late_checkout_request', match: /(late checkout|late check.?out|extend (stay|checkout)|check.?out at \d)/i },
  // Housekeeping
  { type: 'housekeeping', subtype: 'extra_towels', match: /(extra towels?|more towels?|fresh towels?)/i },
  { type: 'housekeeping', subtype: 'mid_stay_clean', match: /(mid.?stay clean|cleaning during stay|stay (re)?clean)/i },
  { type: 'housekeeping', subtype: 'linen_change', match: /(linen|bed sheets?|change sheets?)/i },
  { type: 'housekeeping', subtype: 'urgent_clean', match: /(urgent clean|clean now|immediate clean)/i },
  // Transport
  { type: 'transport', subtype: 'airport_pickup', match: /(airport pickup|airport pick.?up|pick.?up from airport)/i },
  { type: 'transport', subtype: 'airport_dropoff', match: /(airport drop.?off|drop.?off (to|at) airport|airport transfer.*(out|depart))/i },
  { type: 'transport', subtype: 'tour_booking', match: /(tour|excursion|sightseeing)/i },
  { type: 'transport', subtype: 'car_rental', match: /(car rental|rent a car|car hire)/i },
  // Booking
  { type: 'booking_inquiry', subtype: 'availability', match: /(availability|available (dates|nights)|dates available|book.*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec))/i },
  { type: 'booking_inquiry', subtype: 'price_match', match: /(price match|cheaper (rate|price)|lower price|rate question|price question)/i },
  // In-stay
  { type: 'in_stay_support', subtype: 'comfort_issue', match: /(uncomfortable|comfort (issue|problem)|too (hot|cold|warm))/i },
  { type: 'in_stay_support', subtype: 'special_request', match: /(special request|special occasion|anniversary|birthday|honeymoon)/i },
  // Reviews
  { type: 'review', subtype: 'reply_needed', match: /(review reply|reply to review)/i },
  { type: 'review', subtype: 'follow_up', match: /(review follow.?up|low rating|bad review)/i },
];

const lookupTemplate = (type, subtype) => {
  if (type === 'other') return TITLE_TEMPLATES.other;
  const group = TITLE_TEMPLATES[type];
  if (!group) return TITLE_TEMPLATES.other;
  return group[subtype] || group.general || TITLE_TEMPLATES.other;
};

/**
 * Detect the request_type + subtype from category enum and summary text.
 */
export const detectTaskKind = ({ category, summary }) => {
  const text = String(summary || '');
  // 1. Try summary-based subtype rules first (most specific)
  for (const rule of SUBTYPE_RULES) {
    if (rule.match.test(text)) {
      return { type: rule.type, subtype: rule.subtype };
    }
  }
  // 2. Fall back to category mapping with general subtype
  const type = CATEGORY_MAP[String(category || '').toUpperCase()] || 'other';
  return { type, subtype: 'general' };
};

/**
 * Build a concierge-style title for a task/request.
 * Prepends guest first name when present.
 */
export const buildTaskTitle = ({ category, summary, guest_name } = {}) => {
  const { type, subtype } = detectTaskKind({ category, summary });
  const template = lookupTemplate(type, subtype);
  const first = (guest_name || '').trim().split(/\s+/)[0];
  if (first) return `${first}: ${template}`;
  return template;
};
